import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import { COMSAPP_APP_URL } from './config';
import {
  getLinkedAccounts,
  linkComsappAccountRequest,
  linkComsappAccountByIdRequest,
  unlinkComsappAccountRequest,
  createComsappAccountRequest,
} from '../utils/comsHub';
import './AccountLinkingPanel.css';

const APP_LABELS = {
  comsapp: 'ComSapp',
  'rs-education': 'RS Education',
  koyoga: 'Koyoga',
  cip: 'Civic Intelligence Platform',
};

const normalizeUsernamePart = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/\.+/g, '.')
    .replace(/^\.+|\.+$/g, '');

const buildSuggestedUsername = (user) => {
  const first = normalizeUsernamePart(user?.firstName);
  const last = normalizeUsernamePart(user?.lastName);
  const emailLocal = normalizeUsernamePart((user?.email || '').split('@')[0]);
  const userIdTail = String(user?.id || user?._id || '').replace(/[^a-z0-9]/gi, '').slice(-6).toLowerCase();

  let base = `${first}${last}` || emailLocal || 'rsuser';
  if (base.length < 3) {
    base = `${base}${userIdTail || '001'}`;
  }
  if (base.length < 3) {
    base = `${base}user`;
  }
  return base.slice(0, 32);
};

const generateStrongPassword = (length = 14) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
  let result = '';
  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const AccountLinkingPanel = () => {
  const { currentUser, refreshUser } = useContext(AuthContext);

  const buildDefaultCreateForm = () => {
    const generatedPassword = generateStrongPassword();
    return {
      email: currentUser?.email || '',
      username: buildSuggestedUsername(currentUser),
      password: generatedPassword,
      confirmPassword: generatedPassword,
    };
  };

  const comsappLoginUrl = COMSAPP_APP_URL
    ? `${COMSAPP_APP_URL.replace(/\/$/, '')}/login`
    : '';

  const [linkedAccounts, setLinkedAccounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Link modal — toggle between email and ID
  const [linkMode, setLinkMode] = useState('id'); // 'id' | 'email'
  const [linkEmail, setLinkEmail] = useState('');
  const [linkId, setLinkId] = useState('');
  const [linking, setLinking] = useState(false);

  const [createForm, setCreateForm] = useState(() => buildDefaultCreateForm());
  const [creating, setCreating] = useState(false);
  const [rateLimitUntil, setRateLimitUntil] = useState(0);
  const [rateLimitNow, setRateLimitNow] = useState(Date.now());

  const rateLimitSecondsRemaining = Math.max(0, Math.ceil((rateLimitUntil - rateLimitNow) / 1000));
  const isRateLimited = rateLimitSecondsRemaining > 0;

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (!isRateLimited) return undefined;

    const timer = window.setInterval(() => {
      setRateLimitNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isRateLimited]);

  const applyRateLimitState = (result) => {
    if (result?.status !== 429) return false;

    const retrySeconds = Number(result?.retryAfterSeconds);
    const waitSeconds = Number.isFinite(retrySeconds) && retrySeconds > 0 ? retrySeconds : 30;
    const waitMs = waitSeconds * 1000;

    setRateLimitUntil(Date.now() + waitMs);
    setRateLimitNow(Date.now());
    setError(`${result.error || 'Too many integration requests.'} Please wait ${waitSeconds}s and try again.`);
    return true;
  };

  const loadAccounts = async () => {
    setLoading(true);
    const result = await getLinkedAccounts(api);
    if (result.success) {
      setLinkedAccounts(result.accounts);
    }
    setLoading(false);
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setLinkEmail('');
    setLinkId('');
    setError('');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm(buildDefaultCreateForm());
    setError('');
  };

  const openCreateModal = () => {
    setError('');
    setSuccess('');
    setCreateForm(buildDefaultCreateForm());
    setShowCreateModal(true);
  };

  const handleUseGeneratedPassword = async () => {
    const generated = generateStrongPassword();
    setCreateForm((prev) => ({
      ...prev,
      password: generated,
      confirmPassword: generated,
    }));

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(generated);
        setSuccess('Generated password copied to clipboard.');
      }
    } catch {
      // Non-blocking: keep generated password in form even if clipboard is blocked.
    }
  };

  const handleUseRsDefaults = () => {
    const defaults = buildDefaultCreateForm();
    setCreateForm({
      ...defaults,
      // Keep confirm in sync so submit can work immediately.
      confirmPassword: defaults.password,
    });
    setError('');
    setSuccess('RS defaults applied, including a generated password.');
  };

  const handleLinkAccount = async (e) => {
    e.preventDefault();

    if (linking || creating) return;

    if (isRateLimited) {
      setError(`Too many integration requests. Please wait ${rateLimitSecondsRemaining}s and try again.`);
      return;
    }

    setError('');
    setSuccess('');

    let result;
    if (linkMode === 'id') {
      if (!linkId.trim()) { setError('ComSapp user ID is required'); return; }

      const requestedId = linkId.trim();
      const alreadyLinkedById = [
        linkedAccounts?.comsapp?.primary,
        ...(linkedAccounts?.comsapp?.linked || []),
      ].some((acc) => String(acc?.userId || '') === requestedId);

      if (alreadyLinkedById) {
        setSuccess('This ComSapp account is already linked.');
        closeLinkModal();
        return;
      }

      setLinking(true);
      result = await linkComsappAccountByIdRequest(api, requestedId, currentUser?.id);
    } else {
      if (!linkEmail.trim()) { setError('Email address is required'); return; }
      setLinking(true);
      result = await linkComsappAccountRequest(api, linkEmail.trim(), currentUser?.id);
    }

    if (result.success) {
      setSuccess(result.message || 'Account linked successfully!');
      closeLinkModal();
      await loadAccounts();
      await refreshUser();
      window.dispatchEvent(new Event('comsapp-linking-updated'));
    } else {
      if (!applyRateLimitState(result)) {
        setError(result.error || 'Failed to link account');
      }
    }
    setLinking(false);
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();

    if (linking || creating) return;

    if (isRateLimited) {
      setError(`Too many integration requests. Please wait ${rateLimitSecondsRemaining}s and try again.`);
      return;
    }

    const { email, username, password, confirmPassword } = createForm;

    if (!email.trim() || !username.trim() || !password) {
      setError('All fields are required'); return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match'); return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters'); return;
    }

    setCreating(true);
    setError('');
    setSuccess('');

    const result = await createComsappAccountRequest(api, { email, username, password, rsUserId: currentUser?.id });
    if (result.success) {
      setSuccess(result.message || 'Account created and linked successfully!');
      closeCreateModal();
      await loadAccounts();
      await refreshUser();
      window.dispatchEvent(new Event('comsapp-linking-updated'));
    } else {
      if (!applyRateLimitState(result)) {
        setError(result.error || 'Failed to create account');
      }
    }
    setCreating(false);
  };

  const handleUnlink = async (accountId) => {
    if (!window.confirm('Are you sure you want to unlink this account?')) return;
    setError('');
    setSuccess('');
    const result = await unlinkComsappAccountRequest(api, accountId);
    if (result.success) {
      setSuccess(result.message || 'Account unlinked successfully!');
      await loadAccounts();
      await refreshUser();
      window.dispatchEvent(new Event('comsapp-linking-updated'));
    } else {
      setError(result.error || 'Failed to unlink account');
    }
  };

  const comsappAccounts = linkedAccounts.comsapp || {};
  const primaryAccount = comsappAccounts.primary;
  const linkedAccountsList = comsappAccounts.linked || [];

  return (
    <div className="account-linking-panel">
      <div className="account-linking-header">
        <h3>ComSapp Accounts</h3>
        <p className="account-linking-subtitle">Link your ComSapp accounts. Use your ComSapp User ID for the most accurate matching.</p>
        {currentUser?.id && (
          <p className="account-linking-rs-id">Your RS Education ID: <span className="account-linking-id-value">{currentUser.id}</span></p>
        )}
      </div>

      {error && <div className="account-linking-error">{error}</div>}
      {success && <div className="account-linking-success">{success}</div>}
      {isRateLimited && (
        <div className="account-linking-rate-limit">
          Integration rate limit active. Try again in about {rateLimitSecondsRemaining}s.
        </div>
      )}

      {loading ? (
        <div className="account-linking-loading">Loading accounts...</div>
      ) : (
        <div className="account-linking-content">
          {/* Primary Account */}
          {primaryAccount && primaryAccount.userId && (
            <div className="account-linking-section">
              <h4 className="account-linking-label">Primary Account (Auto-Linked)</h4>
              {(primaryAccount.syncStatus === 'pending' || !primaryAccount.syncStatus) && (
                <div className="account-linking-notice pending-notice">
                  Waiting for the ComSapp user to approve this connection in their account.
                </div>
              )}
              <div className="account-linking-card primary">
                <div className="account-info">
                  <div className="account-field">
                    <span className="account-label">USERNAME</span>
                    <span className="account-value">{primaryAccount.username || '-'}</span>
                  </div>
                  <div className="account-field">
                    <span className="account-label">USER ID</span>
                    <span className="account-value account-id-mono">{primaryAccount.userId || '-'}</span>
                  </div>
                  <div className="account-field">
                    <span className="account-label">ROLE</span>
                    <span className="account-value">{primaryAccount.role || 'member'}</span>
                  </div>
                  <div className="account-field">
                    <span className="account-label">STATUS</span>
                    <span className={`account-status ${primaryAccount.syncStatus || 'pending'}`}>
                      {(primaryAccount.syncStatus || 'pending').toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  className="btn-unlink"
                  onClick={() => handleUnlink(primaryAccount.userId)}
                  title="Remove this link"
                >
                  Unlink
                </button>
              </div>
            </div>
          )}

          {/* Manually Linked Accounts */}
          {linkedAccountsList.length > 0 && (
            <div className="account-linking-section">
              <h4 className="account-linking-label">Linked Accounts ({linkedAccountsList.length})</h4>
              {linkedAccountsList.map((account, idx) => (
                <div key={idx} className="account-linking-card linked">
                  {account.syncStatus === 'pending' && (
                    <div className="account-linking-notice pending-notice">
                      Awaiting approval from the ComSapp account holder.
                    </div>
                  )}
                  <div className="account-info">
                    <div className="account-field">
                      <span className="account-label">USERNAME</span>
                      <span className="account-value">{account.username || '-'}</span>
                    </div>
                    <div className="account-field">
                      <span className="account-label">USER ID</span>
                      <span className="account-value account-id-mono">{account.userId || '-'}</span>
                    </div>
                    <div className="account-field">
                      <span className="account-label">TYPE</span>
                      <span className="account-value">{account.linkType || 'manual'}</span>
                    </div>
                    <div className="account-field">
                      <span className="account-label">STATUS</span>
                      <span className={`account-status ${account.syncStatus || 'pending'}`}>
                        {(account.syncStatus || 'pending').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <button
                    className="btn-unlink"
                    onClick={() => handleUnlink(account.userId)}
                    title="Unlink this account"
                  >
                    Unlink
                  </button>
                </div>
              ))}
            </div>
          )}

          {!primaryAccount && linkedAccountsList.length === 0 && (
            <div className="account-linking-empty">
              <p>No ComSapp accounts linked yet.</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="account-linking-actions">
            <button className="btn-link-existing" onClick={() => setShowLinkModal(true)} disabled={linking || creating}>
              Link Existing Account
            </button>
            <button className="btn-create-new" onClick={openCreateModal} disabled={linking || creating}>
              Create New Account
            </button>
            {comsappLoginUrl && (
              <a
                className="btn-open-comsapp"
                href={comsappLoginUrl}
                target="_blank"
                rel="noreferrer"
                title="Open ComSapp login"
              >
                Open ComSapp Login
              </a>
            )}
          </div>
        </div>
      )}

      {/* Link Account Modal */}
      {showLinkModal && (
        <div className="account-linking-modal-backdrop">
          <div className="account-linking-modal">
            <div className="modal-header">
              <h4>Link Existing ComSapp Account</h4>
              <button className="modal-close" onClick={closeLinkModal}>&times;</button>
            </div>
            <form onSubmit={handleLinkAccount}>
              {/* Mode toggle */}
              <div className="link-mode-toggle">
                <button
                  type="button"
                  className={`link-mode-btn ${linkMode === 'id' ? 'active' : ''}`}
                  onClick={() => setLinkMode('id')}
                >
                  By User ID
                </button>
                <button
                  type="button"
                  className={`link-mode-btn ${linkMode === 'email' ? 'active' : ''}`}
                  onClick={() => setLinkMode('email')}
                >
                  By Email
                </button>
              </div>

              {linkMode === 'id' ? (
                <div className="form-group">
                  <label htmlFor="link-id">ComSapp User ID</label>
                  <input
                    id="link-id"
                    type="text"
                    value={linkId}
                    onChange={(e) => setLinkId(e.target.value)}
                    placeholder="e.g. 69e91b1818a9296a1ada3a3a"
                    disabled={linking}
                    autoComplete="off"
                  />
                  <p className="form-hint">Find your ComSapp ID in your ComSapp Account page under your profile.</p>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="link-email">Email Address</label>
                  <input
                    id="link-email"
                    type="email"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                    placeholder="your@example.com"
                    disabled={linking}
                    autoComplete="off"
                  />
                  <p className="form-hint">Enter the email address associated with your ComSapp account.</p>
                </div>
              )}

              {error && <div className="modal-error">{error}</div>}
              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={linking || isRateLimited}>
                  {linking ? 'Linking...' : 'Link Account'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeLinkModal} disabled={linking}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateModal && (
        <div className="account-linking-modal-backdrop">
          <div className="account-linking-modal">
            <div className="modal-header">
              <h4>Create New ComSapp Account</h4>
              <button className="modal-close" onClick={closeCreateModal}>&times;</button>
            </div>
            <form onSubmit={handleCreateAccount}>
              <div className="form-hint form-hint-strong">
                RS profile defaults are prefilled. You can edit before creating the ComSapp account.
              </div>
              <div className="form-group">
                <label htmlFor="create-email">Email Address</label>
                <input
                  id="create-email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="work@example.com"
                  disabled={creating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-username">Username</label>
                <input
                  id="create-username"
                  type="text"
                  value={createForm.username}
                  onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                  placeholder="username"
                  disabled={creating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-password">Password</label>
                <input
                  id="create-password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Min 8 characters"
                  disabled={creating}
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-confirm">Confirm Password</label>
                <input
                  id="create-confirm"
                  type="password"
                  value={createForm.confirmPassword}
                  onChange={(e) => setCreateForm({ ...createForm, confirmPassword: e.target.value })}
                  placeholder="Confirm password"
                  disabled={creating}
                />
              </div>
              <div className="account-linking-quick-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleUseRsDefaults}
                  disabled={creating}
                >
                  Use RS Defaults
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleUseGeneratedPassword}
                  disabled={creating}
                >
                  Generate Password
                </button>
              </div>
              {error && <div className="modal-error">{error}</div>}
              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={creating || isRateLimited}>
                  {creating ? 'Creating...' : 'Create Account'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeCreateModal} disabled={creating}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountLinkingPanel;
